'use client'

import { resolveProductHost } from '@/lib/product-hosts'
import { creditPackPriceUsd } from '@/lib/credit-pack'

import { trackBeginCheckout } from '@/lib/analytics/billing-events'

import { getPurchaseAttribution } from '@/lib/analytics/attribution'
import type { PurchaseAttribution } from '@/lib/analytics/purchase-attribution'

import {
  rememberConnectCheckout,
  consumeConnectCheckout,
} from '@/lib/connect/billing'

import { useCallback, useEffect, useRef, useState } from 'react'
import { mutate as globalMutate } from 'swr'

import { useApiQuery, useApiMutation, useApiInfinite } from './use-api'
import { ApiFetchError, buildQueryString } from './api-fetcher'
import { useSelfAuth } from '@/providers/self-auth-provider'
import { useOptionalActiveOrg } from '@/providers/active-scope-provider'
import { useOrgAdminGate } from '@/hooks/use-org-access'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { orgAdminAllowed } from '@/lib/org-access'
import { buildOrgUrl, parseOrgPath } from '@/lib/routes'
import { useCreditStore } from '@/stores/credit-store'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import { appendCheckoutContext } from '@/lib/analytics/billing-events'
import { resolveRewardfulReferral } from '@/lib/rewardful'
import {
  PRODUCT_HUNT_PROMO_KEY,
  hasProductHuntOffer,
} from '@/lib/product-hunt-offer'
import {
  currentBillingTaskReturn,
  rememberBillingTaskReturn,
  withBillingTaskReturn,
} from '@/lib/billing-task-return'
import type {
  AccountTimelineItem,
  ChatCostEstimate,
  ChatCreditDetail,
  ChatCredits,
  CreditBalance,
  CreditUsageSummary,
  OutsideChatBucket,
  OutsideChatCharge,
  BillingProvider,
  OpenInvoice,
  CancelAndRefundResult,
  RefundEligibility,
  PlanInfo,
  PlanSummary,
  CreditTransaction,
  RedemptionCodePreview,
  RedemptionCodeRedeemResult,
  RedemptionCodeRequest,
  CreditPackCheckoutResult,
  CreditPackPurchase,
  BillingInterval,
  PricingDefaultInterval,
} from '@/lib/billing-types'

/**
 * Canonical credit-balance endpoint. `useCreditBalance` registers its SWR
 * cache under org-scoped variants of this key — `${BALANCE_KEY}?orgScope=unscoped`
 * or `${BALANCE_KEY}?orgScope=<org-slug>` (see the `cacheKey` construction
 * below) — so mutating the bare key never reaches a mounted consumer.
 */
const BALANCE_KEY = '/api/billing/balance'

/**
 * Build an SWR key-filter predicate for a billing endpoint. Matches the bare
 * path or any query-string variant (`${path}?...`) — `useCreditBalance` and
 * `useSubscription` register their caches under `${path}?orgScope=...` keys, so
 * the bare path never reaches a mounted consumer on its own. The exact-or-`?`
 * match (rather than a loose `startsWith(path)`) prevents `/api/billing/plan`
 * from sweeping the sibling `/api/billing/plans` cache. Exported for callers
 * that need the raw predicate (e.g. redemption-code cache invalidation).
 */
export function matchesBillingKey(path: string): (key: unknown) => boolean {
  return (key: unknown): boolean =>
    typeof key === 'string' && (key === path || key.startsWith(`${path}?`))
}

/**
 * SWR key filter matching every `/api/billing/balance` cache-key variant
 * registered by `useCreditBalance` (all orgScope values). Delegates to
 * `matchesBillingKey` so the two predicates cannot drift. Exported for tests
 * and for callers that need the raw predicate.
 */
export const isBalanceKey = matchesBillingKey(BALANCE_KEY)

/**
 * Revalidate every mounted `/api/billing/balance*` SWR consumer (all orgScope
 * variants). Use this — never a bare `mutate(BALANCE_KEY)`, which no consumer
 * registers — after any event that changes the credit balance (charge,
 * insufficient-balance, redemption, upgrade return, workspace ensure).
 */
export function revalidateCreditBalance(): Promise<unknown> {
  return globalMutate(isBalanceKey)
}

/**
 * Fetch credit balance and sync to Zustand store, scoped to the current org.
 * Polls every 60s (no revalidate-on-focus, per §2.4 polling rules). Store
 * writes self-check the cached `orgSlug` and reset on an org mismatch.
 */
export function useCreditBalance() {
  const workspace = useOptionalCurrentBrand()
  const activeOrg = useOptionalActiveOrg()
  const currentOrgSlug = workspace?.orgSlug ?? activeOrg?.slug ?? null
  const setBalance = useCreditStore(s => s.setBalance)
  const setQuota = useCreditStore(s => s.setQuota)
  const setPlan = useCreditStore(s => s.setPlan)
  const setToolGenerationAllowed = useCreditStore(
    s => s.setToolGenerationAllowed,
  )
  const setToolGenerationMode = useCreditStore(s => s.setToolGenerationMode)
  const setOrgSlug = useCreditStore(s => s.setOrgSlug)
  const reset = useCreditStore(s => s.reset)

  useEffect(() => {
    if (currentOrgSlug === null) {
      return
    }
    const liveOrgSlug = useCreditStore.getState().orgSlug
    if (liveOrgSlug !== null && liveOrgSlug !== currentOrgSlug) {
      reset()
    }
    if (useCreditStore.getState().orgSlug !== currentOrgSlug) {
      setOrgSlug(currentOrgSlug)
    }
  }, [currentOrgSlug, reset, setOrgSlug])

  return useApiQuery<CreditBalance>(BALANCE_KEY, {
    cacheKey:
      currentOrgSlug === null
        ? `${BALANCE_KEY}?orgScope=unscoped`
        : `${BALANCE_KEY}?orgScope=${encodeURIComponent(currentOrgSlug)}`,
    revalidateOnFocus: false,
    refreshInterval: 60_000,
    onSuccess: data => {
      // Scope self-check: if the org changed since the last write (e.g.
      // stale closure, multiple tabs), drop the stale balance/quota/plan
      // before writing the fresh values. Mirrors the `brandId` scope-check
      // pattern in `context-resources-store.ts`.
      const liveOrgSlug = useCreditStore.getState().orgSlug
      if (
        currentOrgSlug !== null &&
        liveOrgSlug !== null &&
        currentOrgSlug !== liveOrgSlug
      ) {
        return
      }
      setBalance(data.balance)
      if (data.credits_per_month != null) {
        setQuota(data.credits_per_month)
      }
      setPlan(data.plan_name, data.plan_slug ?? null)
      setToolGenerationAllowed(data.tool_generation_allowed ?? null)
      setToolGenerationMode(data.tool_generation_mode ?? null)
      // Only track a real org — a poll firing while `workspace` is
      // unresolved (e.g. non-brand routes like settings/onboarding, which
      // mount AppRail outside CurrentBrandProvider) must not clobber the
      // scope key to null, or it would silently defeat the mismatch guard
      // above for the next poll.
      if (currentOrgSlug !== null) {
        setOrgSlug(currentOrgSlug)
      }
    },
  })
}

/**
 * Fetch active subscription info.
 */
export function useSubscription(options?: { enabled?: boolean }) {
  const workspace = useOptionalCurrentBrand()
  const activeOrg = useOptionalActiveOrg()
  const currentOrgSlug = workspace?.orgSlug ?? activeOrg?.slug ?? null

  return useApiQuery<PlanInfo | null>('/api/billing/plan', {
    // Billing data is organization-scoped on the server. Scope the SWR cache
    // too, so a client-side org switch cannot momentarily reuse another
    // organization's subscription when evaluating product access.
    cacheKey:
      currentOrgSlug === null
        ? '/api/billing/plan?orgScope=unscoped'
        : `/api/billing/plan?orgScope=${encodeURIComponent(currentOrgSlug)}`,
    revalidateOnFocus: false,
    enabled: options?.enabled,
  })
}

/**
 * The unpaid renewal invoice of a `past_due` subscription. A failed renewal
 * can only be settled on Stripe's hosted invoice page — Checkout and plan
 * changes are both closed to an org that still holds a live subscription.
 */
export function useOpenInvoice(options?: { enabled?: boolean }) {
  const workspace = useOptionalCurrentBrand()
  const activeOrg = useOptionalActiveOrg()
  const currentOrgSlug = workspace?.orgSlug ?? activeOrg?.slug ?? null

  return useApiQuery<OpenInvoice | null>('/api/billing/open-invoice', {
    cacheKey:
      currentOrgSlug === null
        ? '/api/billing/open-invoice?orgScope=unscoped'
        : `/api/billing/open-invoice?orgScope=${encodeURIComponent(currentOrgSlug)}`,
    // The invoice is paid in another tab; coming back should drop the banner.
    revalidateOnFocus: true,
    enabled: options?.enabled,
  })
}

/**
 * Whether this org may cancel now with a full refund (14-day guarantee).
 * Admin-only on the server, like the portal and cancel endpoints; callers gate
 * it with `useBillingAdminGate` and only ask for a Stripe-billed paid plan.
 */
export function useRefundEligibility(options?: { enabled?: boolean }) {
  const workspace = useOptionalCurrentBrand()
  const activeOrg = useOptionalActiveOrg()
  const currentOrgSlug = workspace?.orgSlug ?? activeOrg?.slug ?? null

  return useApiQuery<RefundEligibility>('/api/billing/refund-eligibility', {
    cacheKey:
      currentOrgSlug === null
        ? '/api/billing/refund-eligibility?orgScope=unscoped'
        : `/api/billing/refund-eligibility?orgScope=${encodeURIComponent(currentOrgSlug)}`,
    revalidateOnFocus: false,
    enabled: options?.enabled,
  })
}

/**
 * Refund the current period in full and end the subscription now. The server
 * re-checks eligibility; the plan, balance and eligibility caches are all
 * revalidated afterwards so the page shows Free straight away.
 */
export function useCancelAndRefund() {
  const mutation = useApiMutation<CancelAndRefundResult, Record<string, never>>(
    '/api/billing/cancel-and-refund',
  )

  const cancelAndRefund = useCallback(async () => {
    try {
      return await mutation.trigger({ body: {} })
    } finally {
      await revalidateCreditBalance()
      await globalMutate(
        (key: unknown) =>
          typeof key === 'string' && key.includes('/api/billing/'),
      )
    }
  }, [mutation])

  return { cancelAndRefund, isMutating: mutation.isMutating }
}

/**
 * Which side bills this workspace, plus the merchant-facing manage URL.
 *
 * Separate from `useSubscription`, which is null on the Free tier — and a
 * Shopify-billed workspace on Free is exactly the case the UI must get right:
 * every Soku-side payment endpoint 403s for it, so rendering a Stripe checkout
 * button would be an button that cannot work.
 */
export function useBillingProvider(options?: { enabled?: boolean }) {
  const workspace = useOptionalCurrentBrand()
  const activeOrg = useOptionalActiveOrg()
  const currentOrgSlug = workspace?.orgSlug ?? activeOrg?.slug ?? null

  return useApiQuery<BillingProvider>('/api/billing/provider', {
    cacheKey:
      currentOrgSlug === null
        ? '/api/billing/provider?orgScope=unscoped'
        : `/api/billing/provider?orgScope=${encodeURIComponent(currentOrgSlug)}`,
    revalidateOnFocus: false,
    // PricingDialog also mounts on anonymous marketing pages, where this
    // endpoint has no organization to answer for. Callers there pass the same
    // signed-in gate `usePlans` already uses, so a logged-out visitor does not
    // spend a doomed request (and an SWR retry loop) on every page load.
    enabled: options?.enabled,
  })
}

/**
 * Fetch available billing plans.
 */
export function usePlans(options?: { enabled?: boolean }) {
  return useApiQuery<PlanSummary[]>('/api/billing/plans', {
    revalidateOnFocus: false,
    enabled: options?.enabled,
  })
}

/**
 * Which billing interval the pricing dialog preselects, for the
 * `pricing-default-interval` experiment.
 *
 * `enabled` must be true only where the treatment is actually visible — the
 * pricing dialog OPEN. The POST deals and persists the org's arm, so calling it
 * from somewhere the dialog merely exists (it is mounted on every authenticated
 * layout) would put people who never saw a price into both cohorts and make the
 * experiment unreadable.
 *
 * Asked once per mount and never re-asked: re-rolling the default under
 * someone who has already looked at the toggle would be a worse bug than the
 * one being tested. `settled` is what the caller waits on before rendering the
 * toggle, so a control-then-monthly flip is never visible.
 *
 * A failure is not surfaced anywhere. An experiment that cannot answer falls
 * back to the unassigned default — monthly — not to an error the customer
 * should see on a paywall.
 */
export function usePricingDefaultInterval(options: { enabled: boolean }) {
  const { enabled } = options
  const { trigger } = useApiMutation<PricingDefaultInterval>(
    '/api/billing/pricing-default-interval',
    { suppressGlobalErrorHandler: true },
  )
  const [data, setData] = useState<PricingDefaultInterval | null>(null)
  const [settled, setSettled] = useState(false)
  const asked = useRef(false)

  useEffect(() => {
    if (!enabled || asked.current) return
    asked.current = true
    // No abort-on-cleanup guard here, and that is the point. `enabled` goes
    // false as soon as the dialog closes, so a guard would drop the response
    // of a request that had already been sent — while `asked.current` stayed
    // true, making the arm unaskable for the rest of the mount and leaving
    // `settled` false forever. The caller withholds the interval toggle until
    // `settled`, so that combination does not degrade the experiment, it
    // removes the customer's ability to change billing interval at all.
    //
    // The dialog is mounted for the lifetime of its page (the store, not the
    // mount, controls `isOpen`), so landing the result after a close is both
    // safe and useful: the arm is ready the instant it is reopened.
    void trigger({})
      .then(result => {
        if (result) setData(result)
      })
      .catch(() => {
        // Status quo. Deliberately silent — see the doc comment.
      })
      .finally(() => {
        setSettled(true)
      })
  }, [enabled, trigger])

  return { data, settled }
}

/**
 * Page size shared by the two billing list endpoints (`/transactions` and
 * `/timeline`), both of which are `page` / `page_size` + `ResponseSchema.paginated`.
 */
const BILLING_LIST_PAGE_SIZE = 20

interface PaginatedBillingList<T> {
  items: T[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  loadMore: () => void
  error: Error | undefined
  /** Revalidate from page one, so a failed list can be retried in place. */
  mutate: () => Promise<unknown>
}

/**
 * Cumulative page-numbered accumulation for a billing list endpoint.
 *
 * These endpoints are page-numbered and their total lives in the response
 * envelope the fetch layer strips, so "more exists" is inferred from a full
 * page: a short page ends the list; an exact-multiple total costs one extra
 * empty fetch and then stops. Changing `filter` rewrites the page-1 key,
 * which resets the accumulated pages (`useApiInfinite` contract) — so the
 * filter is folded into the key as a pre-serialized string rather than an
 * object whose identity would churn every render.
 */
function usePaginatedBillingList<T>(
  path: string,
  filter?: Record<string, string>,
  options?: { enabled?: boolean },
): PaginatedBillingList<T> {
  const enabled = options?.enabled ?? true
  const filterQuery = buildQueryString(filter)
  const getKey = useCallback(
    (pageIndex: number, previous: T[] | null) => {
      if (!enabled) return null
      if (previous && previous.length < BILLING_LIST_PAGE_SIZE) return null
      const paging = buildQueryString({
        page: String(pageIndex + 1),
        page_size: String(BILLING_LIST_PAGE_SIZE),
      })
      return `${path}?${paging}${filterQuery ? `&${filterQuery}` : ''}`
    },
    [enabled, path, filterQuery],
  )
  const {
    data: pages,
    error,
    size,
    setSize,
    isValidating,
    mutate,
  } = useApiInfinite<T[]>(getKey, { revalidateOnFocus: false })

  const items = (pages ?? []).flat()
  const lastPage = pages?.[pages.length - 1]
  const hasMore = !!lastPage && lastPage.length === BILLING_LIST_PAGE_SIZE
  const isLoading = enabled && !pages && !error
  const isLoadingMore =
    isValidating && !!pages && typeof pages[size - 1] === 'undefined'
  const loadMore = useCallback(() => {
    if (!enabled) return
    void setSize(size + 1)
  }, [enabled, setSize, size])

  return { items, isLoading, isLoadingMore, hasMore, loadMore, error, mutate }
}

/**
 * Cumulative row-level transaction ledger with optional type narrowing.
 *
 * Retained for row-level reconciliation: it is the only view where every
 * single per-charge deduction is visible. The billing page itself renders
 * {@link useAccountTimeline} instead, which folds those deductions into one
 * row per day.
 */
export function useTransactionHistory(transactionType?: string) {
  const { items, ...rest } = usePaginatedBillingList<CreditTransaction>(
    '/api/billing/transactions',
    transactionType ? { transaction_type: transactionType } : undefined,
  )
  return { transactions: items, ...rest }
}

/**
 * Cumulative account activity: real credit movements merged with one rolled-up
 * row per day of usage deductions.
 *
 * `transactionType` is three-state, matching the endpoint:
 *   - omitted — both streams merged (the default view);
 *   - `'deduction'` — ONLY the `daily_usage` rows. The ledger's `deduction`
 *     rows are exactly what the daily rollup folds, so filtering to the
 *     largest category returns the rollups, never an empty list;
 *   - any other type — only ledger rows of that type.
 */
export function useAccountTimeline(
  transactionType?: string,
  options?: { enabled?: boolean },
) {
  return usePaginatedBillingList<AccountTimelineItem>(
    '/api/billing/timeline',
    transactionType ? { transaction_type: transactionType } : undefined,
    options,
  )
}

/**
 * Trailing windows the credit-usage card offers, in inclusive days.
 */
export const CREDIT_USAGE_WINDOWS = [7, 30, 90] as const

export type CreditUsageWindow = (typeof CREDIT_USAGE_WINDOWS)[number]

/**
 * Inclusive UTC `start_date` / `end_date` for a trailing window.
 *
 * Built from UTC (`toISOString`), not from the viewer's local calendar: the
 * server's window is UTC, and a local-midnight round-trip would ask a viewer
 * east of UTC for a day the server has not reached yet.
 *
 * Recomputed each render, which is safe because the caller folds it into an
 * SWR key as a serialized string — the value only changes when the UTC day
 * does, so the key is stable within a day.
 */
function creditUsageWindowParams(days: number): Record<string, string> {
  const end = new Date()
  const start = new Date(end)
  start.setUTCDate(start.getUTCDate() - (days - 1))
  const utcDay = (value: Date) => value.toISOString().slice(0, 10)
  return { start_date: utcDay(start), end_date: utcDay(end) }
}

/**
 * The current org's slug, or `unscoped`, for an SWR cache key.
 *
 * These three URLs carry only a date window, so without this every
 * organization an admin can reach shares one cache entry and a client-side
 * switch can render the previous workspace's chat titles and spend under the
 * new one. The server scopes each response by the caller's organization;
 * `useSubscription` scopes its cache the same way for the same reason.
 */
function useCreditUsageOrgScope(): string {
  const workspace = useOptionalCurrentBrand()
  return workspace?.orgSlug ?? 'unscoped'
}

/**
 * Window totals for the credit-usage card.
 *
 * `chatCredits` plus every `other` bucket equals `credits`, so the header
 * total and the rows under it can never disagree. Each bucket's charges are
 * listed by {@link useOutsideChatCharges}.
 */
export function useCreditUsageSummary(
  days: number,
  options?: { enabled?: boolean },
) {
  const query = buildQueryString(creditUsageWindowParams(days))
  const orgScope = useCreditUsageOrgScope()
  return useApiQuery<CreditUsageSummary>(`/api/billing/usage?${query}`, {
    cacheKey: `/api/billing/usage?${query}&orgScope=${encodeURIComponent(orgScope)}`,
    revalidateOnFocus: false,
    enabled: options?.enabled,
  })
}

/**
 * What an average chat of this org costs, for the out-of-credits dialog's
 * "≈ N chats" line on each plan. `null` data means too few chats to average.
 */
export function useChatCostEstimate(options?: { enabled?: boolean }) {
  const orgScope = useCreditUsageOrgScope()
  return useApiQuery<ChatCostEstimate | null>(
    '/api/billing/usage/chat-estimate',
    {
      cacheKey: `/api/billing/usage/chat-estimate?orgScope=${encodeURIComponent(orgScope)}`,
      revalidateOnFocus: false,
      enabled: options?.enabled,
    },
  )
}

/**
 * Cumulative list of the chats that spent credits, most expensive first.
 *
 * The infinite key is the request URL, so unlike the two hooks around it the
 * org scope travels as a query parameter rather than as a cache-only key. The
 * endpoint resolves the organization from the caller and ignores it.
 */
export function useChatCreditUsage(
  days: number,
  options?: { enabled?: boolean },
) {
  const orgScope = useCreditUsageOrgScope()
  const { items, ...rest } = usePaginatedBillingList<ChatCredits>(
    '/api/billing/usage/chats',
    { ...creditUsageWindowParams(days), orgScope },
    options,
  )
  return { chats: items, ...rest }
}

/**
 * Cumulative list of the charges in one outside-chat bucket, newest first.
 * Org-scoped the same way as {@link useChatCreditUsage}.
 */
export function useOutsideChatCharges(bucket: OutsideChatBucket, days: number) {
  const orgScope = useCreditUsageOrgScope()
  const { items, ...rest } = usePaginatedBillingList<OutsideChatCharge>(
    `/api/billing/usage/buckets/${bucket}`,
    { ...creditUsageWindowParams(days), orgScope },
  )
  return { charges: items, ...rest }
}

/**
 * One chat's spend split by Q&A round. Null `conversationId` keeps the hook
 * mounted (stable hook order) with no request.
 */
export function useChatCreditDetail(
  conversationId: string | null,
  days: number,
  options?: { enabled?: boolean },
) {
  const query = buildQueryString(creditUsageWindowParams(days))
  const orgScope = useCreditUsageOrgScope()
  return useApiQuery<ChatCreditDetail>(
    conversationId
      ? `/api/billing/usage/chats/${conversationId}?${query}`
      : null,
    {
      cacheKey: conversationId
        ? `/api/billing/usage/chats/${conversationId}?${query}&orgScope=${encodeURIComponent(orgScope)}`
        : undefined,
      enabled: (options?.enabled ?? true) && conversationId !== null,
      revalidateOnFocus: false,
    },
  )
}

export function usePreviewRedemptionCode() {
  return useApiMutation<RedemptionCodePreview, RedemptionCodeRequest>(
    '/api/billing/redemption-codes/preview',
  )
}

export function useRedeemRedemptionCode() {
  return useApiMutation<RedemptionCodeRedeemResult, RedemptionCodeRequest>(
    '/api/billing/redemption-codes/redeem',
  )
}

export function useCreditPackPurchase(
  purchaseId: string | null,
  options?: { enabled?: boolean },
) {
  return useApiQuery<CreditPackPurchase>(
    purchaseId ? `/api/billing/credits/purchases/${purchaseId}` : null,
    {
      enabled: options?.enabled ?? purchaseId !== null,
      revalidateOnFocus: false,
      // Checkout confirmation is the only route that enables this short poll.
      // Stop as soon as the purchase leaves `pending` so it cannot become a
      // background request loop after the user reaches a terminal result.
      refreshInterval: (latest?: CreditPackPurchase) =>
        latest === undefined || latest.status === 'pending' ? 1_500 : 0,
    },
  )
}

export function useCreateCreditTopUpCheckout() {
  const { user } = useSelfAuth()
  const taskReturnRef = useRef<{ org: string; path: string | null } | null>(
    null,
  )
  const mutation = useApiMutation<
    CreditPackCheckoutResult,
    {
      credits: number
      success_url: string
      cancel_url: string
      referral?: string
      attribution?: PurchaseAttribution
    }
  >('/api/billing/credits/checkout', {
    onSuccess: data => {
      if (taskReturnRef.current && user) {
        rememberBillingTaskReturn(
          taskReturnRef.current.org,
          user.id,
          taskReturnRef.current.path,
        )
      }
      window.location.href = data.checkout_url
    },
  })
  const workspace = useOptionalCurrentBrand()
  const checkoutStartingRef = useRef(false)
  const [isResolvingReferral, setIsResolvingReferral] = useState(false)

  const createCheckout = useCallback(
    async (
      credits: number,
      returns?: { successUrl: string; cancelUrl: string },
    ) => {
      if (checkoutStartingRef.current || mutation.isMutating) return
      checkoutStartingRef.current = true
      setIsResolvingReferral(true)
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const pathname =
        typeof window !== 'undefined' ? window.location.pathname : ''
      const orgSlug = workspace?.orgSlug ?? parseOrgPath(pathname)?.orgSlug
      const taskReturn = orgSlug ? currentBillingTaskReturn(orgSlug) : null
      taskReturnRef.current = orgSlug
        ? { org: orgSlug, path: returns ? null : taskReturn }
        : null
      const successPath = resolveBillingReturnPath(
        workspace,
        'billing/success',
        pathname,
      )
      const cancelPath = resolveBillingReturnPath(
        workspace,
        'billing/cancel',
        pathname,
      )
      if (workspace) {
        // Top-up sessions always use these return URLs; an older abandoned
        // subscription continuation must not capture an unrelated top-up.
        consumeConnectCheckout(workspace.orgSlug)
      }
      try {
        const referral = await resolveRewardfulReferral()
        if (
          resolveProductHost(window.location.host).product === 'toolfare' ||
          returns?.successUrl.includes('connect')
        ) {
          trackBeginCheckout({
            slug: 'credit_pack',
            interval: 'one_time',
            value: creditPackPriceUsd(credits),
            source: 'connect',
          })
        }
        return await mutation.trigger({
          body: {
            credits,
            attribution: getPurchaseAttribution(),
            success_url: returns
              ? new URL(returns.successUrl, origin).href
              : withBillingTaskReturn(`${origin}${successPath}`, taskReturn),
            cancel_url: returns
              ? new URL(returns.cancelUrl, origin).href
              : withBillingTaskReturn(`${origin}${cancelPath}`, taskReturn),
            ...(referral ? { referral } : {}),
          },
        })
      } finally {
        checkoutStartingRef.current = false
        setIsResolvingReferral(false)
      }
    },
    [mutation, workspace],
  )

  return {
    createCheckout,
    isMutating: mutation.isMutating || isResolvingReferral,
  }
}

/**
 * Resolve an org-scoped billing path (e.g. `'billing/success'`).
 *
 * Billing lives in the org workspace (`/o/{orgSlug}/billing*`) — no brand
 * context is needed. Resolution order, most → least specific:
 *   1. the org of the active brand from React context (`CurrentBrandProvider`);
 *   2. the org parsed from the current pathname — keeps the URL correct even
 *      when the caller is mounted OUTSIDE the provider (e.g. a `PricingDialog`
 *      rendered by the org-level sidebar shell).
 *
 * `pathname` is injected for testability; production callers pass
 * `window.location.pathname`.
 */
export function resolveBillingReturnPath(
  workspace: { orgSlug: string } | null,
  sub: string,
  pathname: string,
): string {
  // A Connect branch used to sit here, ahead of everything else: the Connect
  // console lived outside CurrentBrandProvider on a non-`/o/{org}` path, so
  // both branches below missed it and a customer who had just paid landed on
  // the site root. That console is retired and it was the only surface that
  // mounted a PricingDialog outside the app, so no checkout can start from a
  // Connect path any more and the branch had no reachable input left.
  if (workspace) {
    return buildOrgUrl(workspace.orgSlug, sub)
  }
  const fromOrg = parseOrgPath(pathname)
  if (fromOrg) {
    return buildOrgUrl(fromOrg.orgSlug, sub)
  }
  // Last resort: home — never an org-less billing path that would 404.
  return '/'
}

/**
 * Pure decision behind `useBillingAdminGate`. Billing write access and
 * org-workspace visibility are the same predicate — the server gates both on
 * `require_org_admin` — so this is a re-export of the shared one in
 * `lib/org-access.ts` rather than a second copy of the precedence rules.
 * Kept under its original name because existing call sites and
 * `tests/unit/billing-admin-gate.test.ts` import it from here.
 */
export const billingAdminAllowed = orgAdminAllowed

/**
 * Whether the current user may call the ADMIN-ONLY billing endpoints —
 * `POST /api/billing/portal` and `POST /api/billing/cancel` — which remain
 * `RequireOrgAdmin` on the server, authoritative and fail-closed.
 *
 * Checkout is NOT one of them any more. `POST /api/billing/checkout` and
 * `POST /api/billing/credits/checkout` take `RequireOrgMember`, so the pricing
 * dialog and the top-up dialog no longer consult this gate: paying only ever
 * adds capacity, and the member who hit the wall is the one who needs to clear
 * it. What is still gated here is the half that can take capacity away —
 * Stripe's portal (payment methods, invoices, its own cancel flow) and the
 * direct cancel endpoint.
 *
 * Decision logic: `orgAdminAllowed`.
 */
export function useBillingAdminGate(): boolean {
  return useOrgAdminGate()
}

/**
 * Create a Stripe Checkout session and redirect.
 */
/**
 * Why a Checkout did not start, in the server's own vocabulary.
 *
 * `checkout_awaiting_payment` and `checkout_settling` mean a charge is already
 * in flight, so retrying cannot help; everything else is a genuine retry.
 */
export type CheckoutOutcome = { ok: true } | { ok: false; code: string }

export function useCreateCheckout() {
  const { user } = useSelfAuth()
  const taskReturnRef = useRef<{ org: string; path: string | null } | null>(
    null,
  )
  const mutation = useApiMutation<
    { checkout_url: string },
    {
      plan_id: string
      billing_interval: BillingInterval
      success_url: string
      cancel_url: string
      attribution?: PurchaseAttribution
      referral?: string
      promo?: typeof PRODUCT_HUNT_PROMO_KEY
    }
  >('/api/billing/checkout', {
    onSuccess: data => {
      if (data.checkout_url) {
        if (taskReturnRef.current && user) {
          rememberBillingTaskReturn(
            taskReturnRef.current.org,
            user.id,
            taskReturnRef.current.path,
          )
        }
        window.location.href = data.checkout_url
      }
    },
  })

  // Billing lives in the org workspace. Resolve an org-scoped return URL
  // from context, with a pathname fallback for callers mounted outside
  // CurrentBrandProvider.
  const workspace = useOptionalCurrentBrand()

  // Optional return-URL overrides set by the caller that opened the dialog
  // (e.g. a marketing tool page that wants the user back on it after checkout).
  const overrideSuccess = useBillingDialogStore(s => s.successUrl)
  const overrideCancel = useBillingDialogStore(s => s.cancelUrl)
  const checkoutStartingRef = useRef(false)
  const [isResolvingReferral, setIsResolvingReferral] = useState(false)

  /**
   * Reports whether a Checkout redirect is under way, and why not.
   *
   * It has to report failure, because nothing else does: `handleApiError`
   * only raises a toast for a 500, and this hook's consumer (the pricing
   * dialog) also mounts on marketing pages that render no `<Toaster/>`. A
   * rejected Checkout — a 400 from the intent guard, a Stripe preflight
   * error — used to be swallowed here and reached the customer as a button
   * that did nothing at all.
   *
   * The `code` is carried out, not just a boolean: the server distinguishes
   * "retry, it may work" from "your payment already landed" and "your payment
   * is still settling", and telling the second one to try again is the single
   * piece of advice that cannot help them.
   */
  const createCheckout = useCallback(
    async (
      planId: string,
      billingInterval: BillingInterval,
      // Optional conversion context carried across the Stripe round-trip so the
      // return page can fire a plan-aware Purchase (Meta/GA).
      meta?: {
        planSlug?: string
        value?: number
        successUrl?: string
        cancelUrl?: string
        source?: 'connect'
        entry?: string
        reason?: string
      },
    ): Promise<CheckoutOutcome> => {
      // A redirect is already being started; the caller has nothing to report.
      if (checkoutStartingRef.current || mutation.isMutating)
        return { ok: true }
      checkoutStartingRef.current = true
      setIsResolvingReferral(true)

      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const pathname =
        typeof window !== 'undefined' ? window.location.pathname : ''
      const toAbsolute = (u: string) =>
        u.startsWith('http') ? u : `${origin}${u}`

      // Caller override (e.g. a marketing tool page returning the user to
      // itself) wins; otherwise resolve a brand-scoped path that never 404s.
      const successOverride = meta?.successUrl ?? overrideSuccess
      const cancelOverride = meta?.cancelUrl ?? overrideCancel
      const orgSlug = workspace?.orgSlug ?? parseOrgPath(pathname)?.orgSlug
      const taskReturn = orgSlug ? currentBillingTaskReturn(orgSlug) : null
      taskReturnRef.current = orgSlug
        ? {
            org: orgSlug,
            path: successOverride || cancelOverride ? null : taskReturn,
          }
        : null
      const baseSuccessUrl = successOverride
        ? toAbsolute(successOverride)
        : withBillingTaskReturn(
            `${origin}${resolveBillingReturnPath(workspace, 'billing/success', pathname)}`,
            taskReturn,
          )
      const successUrl = appendCheckoutContext(baseSuccessUrl, {
        plan: meta?.planSlug,
        interval: billingInterval,
        value: meta?.value,
      })
      const baseCancelUrl = cancelOverride
        ? toAbsolute(cancelOverride)
        : withBillingTaskReturn(
            `${origin}${resolveBillingReturnPath(workspace, 'billing/cancel', pathname)}`,
            taskReturn,
          )

      const cancel = new URL(baseCancelUrl)
      if (meta?.source === 'connect') {
        cancel.searchParams.set('interval', billingInterval)
        if (meta.planSlug) cancel.searchParams.set('plan', meta.planSlug)
      }
      const cancelUrl = cancel.href
      if (workspace) {
        if (meta?.source === 'connect') {
          const resume = new URL(cancelUrl)
          resume.searchParams.delete('checkout')
          rememberConnectCheckout(
            workspace.orgSlug,
            `${resume.pathname}${resume.search}`,
          )
        } else consumeConnectCheckout(workspace.orgSlug)
      }
      try {
        const referral = await resolveRewardfulReferral()
        const result = await mutation.trigger({
          body: {
            ...(meta?.source
              ? { source: meta.source, entry: meta.entry, reason: meta.reason }
              : {}),
            plan_id: planId,
            billing_interval: billingInterval,
            success_url: successUrl,
            cancel_url: cancelUrl,
            attribution: getPurchaseAttribution(),
            ...(referral ? { referral } : {}),
            // Product Hunt arrivals get the launch code pre-applied. The
            // server re-checks the offer window and falls back to the manual
            // promo-code field if Stripe will not take it.
            ...(hasProductHuntOffer() ? { promo: PRODUCT_HUNT_PROMO_KEY } : {}),
          },
        })
        // A 2xx with no URL redirects nowhere, which looks identical to a
        // failure from where the customer is sitting. Report it as one.
        return result?.checkout_url
          ? { ok: true }
          : { ok: false, code: 'missing_checkout_url' }
      } catch (error) {
        // `handleApiError` has logged it; the caller owns telling the user.
        return {
          ok: false,
          code:
            error instanceof ApiFetchError
              ? (error.code ?? 'unknown')
              : 'unknown',
        }
      } finally {
        checkoutStartingRef.current = false
        setIsResolvingReferral(false)
      }
    },
    [mutation, workspace, overrideSuccess, overrideCancel],
  )

  return {
    createCheckout,
    isMutating: mutation.isMutating || isResolvingReferral,
  }
}

/**
 * Create a Stripe Customer Portal session and redirect.
 */
/**
 * Force a Partner API read-back of this workspace's Shopify subscription.
 *
 * Shopify sends no webhook on a plan change and its post-selection redirect is
 * unsigned, so the authoritative read runs behind the session. The Billing page
 * fires this on mount for a Shopify-billed workspace; the 15-minute reconciler
 * covers the merchant who never comes back to the page.
 */
export function useSyncShopifyBilling() {
  return useApiMutation<BillingProvider, Record<string, never>>(
    '/api/billing/shopify/sync',
    {
      onSuccess: () => {
        void revalidateCreditBalance()
      },
    },
  )
}

export function useCreatePortalSession(options?: {
  onError?: (error: Error) => void
}) {
  const onError = options?.onError

  const mutation = useApiMutation<
    { portal_url: string },
    { return_url: string }
  >('/api/billing/portal', {
    onSuccess: data => {
      if (data.portal_url) {
        window.location.href = data.portal_url
      }
    },
    // The portal 400s when the org has no Stripe customer (e.g. an
    // admin-granted plan). The global handler only toasts on 500, so
    // without this the click is silently swallowed and the button reads
    // as broken. Callers surface the failure. SWR reads this at settle
    // time, so passing it straight through stays current without a ref.
    onError,
  })

  // Billing lives in the org workspace. Resolve an org-scoped return URL
  // from context, with a pathname fallback for callers mounted outside
  // CurrentBrandProvider.
  const workspace = useOptionalCurrentBrand()

  const openPortal = useCallback(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : ''
    mutation.trigger({
      body: {
        return_url: `${origin}${resolveBillingReturnPath(
          workspace,
          'billing',
          pathname,
        )}`,
      },
    })
  }, [mutation, workspace])

  return { openPortal, isMutating: mutation.isMutating }
}

/**
 * Move an org that ALREADY pays us onto a different plan.
 *
 * Deliberately NOT `useCreateCheckout`. Sending an existing subscriber back
 * through Stripe Checkout opens a SECOND live subscription: they get billed
 * twice, and provisioning then fails outright because one org may hold only
 * one active subscription row. The pricing dialog picks between the two based
 * on whether a subscription already exists.
 */
export function usePlanChangePreview() {
  const mutation = useApiMutation<
    {
      effective: string
      amount_due_cents: number
      currency: string
      plan_slug: string
    },
    { plan_id: string; billing_interval: BillingInterval }
  >('/api/billing/change-plan/preview')

  const preview = useCallback(
    (planId: string, billingInterval: BillingInterval) =>
      mutation.trigger({
        body: { plan_id: planId, billing_interval: billingInterval },
      }),
    [mutation],
  )
  return { preview, isPreviewing: mutation.isMutating }
}

export function useChangePlan() {
  const mutation = useApiMutation<
    { effective: string; credits_granted: number; plan_slug: string },
    { plan_id: string; billing_interval: BillingInterval }
  >('/api/billing/change-plan')

  const changePlan = useCallback(
    async (planId: string, billingInterval: BillingInterval) => {
      const result = await mutation.trigger({
        body: { plan_id: planId, billing_interval: billingInterval },
      })
      // The balance moves on an upgrade (topped up to the new allowance) and
      // does not on a downgrade; revalidating covers both without branching.
      await revalidateCreditBalance()
      await globalMutate(
        (key: unknown) =>
          typeof key === 'string' && key.includes('/api/billing/'),
      )
      return result
    },
    [mutation],
  )

  return { changePlan, isMutating: mutation.isMutating }
}
